<template>
  <!-- Sidebar Wrapper -->
  <div
      :class="`
      sidebar-wrapper transition-all duration-300
      ${isMobile ? (isSmallMobile ? 'fixed bottom-0 w-full h-[12vh] flex flex-col bg-gray-800' : 'fixed bottom-0 w-full h-[16vh] flex flex-col bg-gray-800') : 'flex flex-col h-full'}
      ${isOpen ? (isMobile ? '' : 'w-96') : (isMobile ? '' : 'w-16')}
    `"
  >
    <!-- Mobile Layout -->
    <template v-if="isMobile">
      <!-- Small Mobile (≤480px): Only show tab options -->
      <template v-if="isSmallMobile">
        <div class="flex w-full h-full bg-gray-800 text-white">
          <!-- Tab Navigation for Small Mobile -->
          <div class="flex w-full justify-around items-center p-2">
            <button 
              @click="setActiveTab('info')" 
              :class="['mobile-tab-btn', { 'active': activeTab === 'info' }]"
            >
              <i class="fas fa-info-circle text-lg"></i>
              <span class="text-xs">Explore</span>
            </button>
            <button 
              @click="setActiveTab('quiz')" 
              :class="['mobile-tab-btn', { 'active': activeTab === 'quiz' }]"
            >
              <i class="fas fa-question-circle text-lg"></i>
              <span class="text-xs">Quiz</span>
            </button>
            <button 
              @click="openPlacesDrawer" 
              :class="['mobile-tab-btn', { 'active': showPlacesDrawer }]"
            >
              <i class="fas fa-history text-lg"></i>
              <span class="text-xs">Places</span>
            </button>
            <button
              v-if="activeTab !== 'quiz'"
              @click="goToNextAmazingPlace"
              class="mobile-next-place-btn"
            >
              <i class="fas fa-globe-americas text-lg"></i>
              <span class="text-xs">Amazing</span>
            </button>
            <button 
              @click="toggleMapSettings" 
              :class="['mobile-tab-btn', { 'active': showMapSettings }]"
            >
              <i class="fas fa-cog text-lg"></i>
              <span class="text-xs">Settings</span>
            </button>
          </div>
        </div>
      </template>
      
      <!-- Regular Mobile (481px-768px): Show content as before -->
      <template v-else>
        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center flex-col w-full h-full text-white">
          <div class="loader mb-4"></div>
        </div>

        <!-- Info Display -->
        <div v-else-if="hasInfoData" class="flex w-full h-1/3 p-1 text-white">
          <!-- Flag (25% of the row) -->
          <div class="flex items-center justify-center w-1/4">
            <img :src="flagUrl" alt="Flag" class="w-12 h-12 object-contain rounded-full" />
          </div>
          <!-- Place Name and Description (75% of the row) -->
          <div class="w-3/4 pl-1">
            <h2 class="text-base font-semibold truncate leading-tight">
              {{ infoData.name || 'Unknown Place' }}{{ infoData.territorial_divisions?.region ? ', ' + infoData.territorial_divisions.region : '' }}{{ infoData.country ? ', ' + infoData.country : '' }}
            </h2>
            <p class="text-xs leading-tight">{{ infoData.description || 'No description available' }}</p>
          </div>
        </div>

        <!-- Buttons Section -->
        <div v-if="hasInfoData">
          <!-- Show More Info Button (Full Row) -->
          <div class="flex w-full h-auto p-1">
            <button
                @click="openModal(infoData.url || '#')"
                class="w-full py-1.5 bg-blue-500 text-white rounded text-center text-sm"
            >
              Show More Info
            </button>
          </div>
          
          <!-- Next Amazing Place Button for Regular Mobile -->
          <div v-if="activeTab !== 'quiz'" class="flex w-full h-auto p-1">
            <button
                @click="goToNextAmazingPlace"
                class="w-full py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded text-center flex items-center justify-center gap-2 text-sm hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-md"
            >
              <i class="fas fa-globe-americas text-sm"></i>
              <span>Explore another amazing place</span>
              <i class="fas fa-arrow-right text-sm"></i>
            </button>
          </div>
        </div>

        <!-- Default Message with Next Amazing Place Button -->
        <div v-else class="flex flex-col items-center justify-center h-full">
          <div class="text-center">
            <div v-if="isInitialLoad" class="mb-1">
              <i class="fas fa-globe-americas text-2xl text-blue-400 mb-1"></i>
              <h3 class="text-base text-white font-semibold mb-1">Welcome to Captain Leo's Maps!</h3>
            </div>
            <p class="text-sm text-gray-400 font-medium text-center mb-2">
              {{ currentLoadingMessage || (isInitialLoad ? `🌍 Taking you on a magical journey around the world...` : `Click on a place to start exploring`)}}
            </p>
            
            <!-- Next Amazing Place Button for Default State -->
            <div class="flex w-full h-auto p-1">
              <button
                  @click="goToNextAmazingPlace"
                  class="w-full py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded text-center flex items-center justify-center gap-2 text-sm hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-md"
              >
                <i class="fas fa-globe-americas text-sm"></i>
                <span>Explore another amazing place</span>
                <i class="fas fa-arrow-right text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Tablet/Desktop Layout -->
    <template v-else>
      <div class="sidebar-header">
        <button @click="toggleSidebar" class="toggle-btn">
          <i :class="`fas ${isOpen ? 'fa-chevron-left' : 'fa-bars'}`"></i>
        </button>
      </div>
      
      <div v-if="isOpen" class="sidebar-content">
        <!-- Modern Tab Interface -->
        <div class="tabs-container">
          <div class="tab-selector">
            <button 
              @click="setActiveTab('info')" 
              :class="['tab-button', { 'active': activeTab === 'info' }]"
            >
              <i class="fas fa-info-circle"></i>
              <span>Explore</span>
            </button>
            <button 
              @click="setActiveTab('quiz')" 
              :class="['tab-button', { 'active': activeTab === 'quiz' }]"
            >
              <i class="fas fa-question-circle"></i>
              <span>Quiz</span>
            </button>
            <button 
              @click="setActiveTab('history')" 
              :class="['tab-button', { 'active': activeTab === 'history' }]"
            >
              <i class="fas fa-history"></i>
              <span>Places</span>
            </button>
            <!-- Settings Button for Tablets -->
            <button 
              v-if="isTablet"
              @click="toggleMapSettings" 
              :class="['tab-button', { 'active': showMapSettings }]"
            >
              <i class="fas fa-cog"></i>
              <span>Settings</span>
            </button>
          </div>
          <div class="tab-indicator" :class="{ 'quiz-active': activeTab === 'quiz', 'history-active': activeTab === 'history', 'settings-active': showMapSettings && isTablet }"></div>
        </div>

        <!-- Scrollable Content Area -->
        <div class="scrollable-content">
          <!-- Content Area -->
          <div class="content-area">
            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
              <div class="modern-loader"></div>
              <p class="loading-text">{{ currentLoadingMessage || 'Loading...' }}</p>
            </div>
            
            <!-- Info Tab Content -->
            <div v-else-if="activeTab === 'info'">
              <div v-if="hasInfoData" class="info-content">
                <InfoTab :infoData="infoData" :flagUrl="flagUrl" @open-modal="openInfoModal" @close="isInfoModalOpen = false" />
              </div>
              <div v-else class="empty-state">
                <div class="empty-icon">
                  <i class="fas fa-globe-americas"></i>
                </div>
                <h3 class="empty-title" v-if="isInitialLoad">Welcome to Captain Leo's Maps!</h3>
                <h3 class="empty-title" v-else>Start Exploring</h3>
                <p class="empty-text" v-if="isInitialLoad">🌍 Taking you on a magical journey around the world...</p>
                <p class="empty-text" v-else>Click anywhere on the map or use the search bar to discover amazing places around the world!</p>
              </div>
            </div>
            
            <!-- Quiz Tab Content -->
            <div v-else-if="activeTab === 'quiz'" class="quiz-content">
              <CountryQuiz @quiz-country-name="updateQuizCountryName" @quiz-type-changed="updateQuizType" @update-score="updateScore" ref="quiz" />
            </div>
            
            <!-- Places History Tab Content -->
            <div v-else-if="activeTab === 'history'" class="history-content">
              <PlacesHistory @navigate-to-place="handleNavigateToPlace" :show-global="!isAuthenticated" />
            </div>
          </div>
        </div>
        
        <!-- Fixed Map Settings Accordion (Bottom of Sidebar) - Hidden on Mobile/Tablet -->
        <div v-if="!isMobile && !isTablet" class="sidebar-footer">
          <!-- Next Amazing Place Button -->
          <div v-if="activeTab !== 'quiz'" class="next-place-container">
            <button @click="goToNextAmazingPlace" class="next-place-btn-secondary">
              <i class="fas fa-globe-americas"></i>
              <span>Explore another amazing place</span>
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>

          <div class="settings-accordion">
            <button @click="toggleMapSettings" class="settings-accordion-header">
              <div class="settings-header-content">
                <i class="fas fa-cog"></i>
                <span>Map Settings</span>
              </div>
              <i :class="`fas fa-chevron-${showMapSettings ? 'up' : 'down'} settings-chevron`"></i>
            </button>
            
            <div v-if="showMapSettings" class="settings-accordion-content">
              <!-- Map Style Selection -->
              <div class="setting-group">
                <label class="setting-label">Map Style</label>
                <div class="radio-group">
                  <label class="radio-option">
                    <input type="radio" name="mapStyle" value="light" v-model="mapSettings.style" />
                    <span>Light</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" name="mapStyle" value="satellite" v-model="mapSettings.style" />
                    <span>Satellite</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" name="mapStyle" value="terrain" v-model="mapSettings.style" />
                    <span>Terrain</span>
                  </label>
                </div>
              </div>
              
              <!-- Country Overlay Settings -->
              <div class="setting-group">
                <label class="setting-label">Country Overlay</label>
                <div class="radio-group">
                  <label class="radio-option">
                    <input type="radio" name="countryOverlay" value="none" v-model="mapSettings.countryOverlay" />
                    <span>None</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" name="countryOverlay" value="white" v-model="mapSettings.countryOverlay" />
                    <span>White Borders</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" name="countryOverlay" value="colored" v-model="mapSettings.countryOverlay" />
                    <span>Colored</span>
                  </label>
                </div>
              </div>
              
              <!-- Place Names Toggle -->
              <div class="setting-group">
                <label class="setting-label">Place Names</label>
                <div class="toggle-group">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="mapSettings.showPlaceNames" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">{{ mapSettings.showPlaceNames ? 'Show' : 'Hide' }}</span>
                  </label>
                </div>
              </div>
              
              <!-- Tectonic Plates Toggle -->
              <div class="setting-group">
                <label class="setting-label">Tectonic Plates</label>
                <div class="toggle-group">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="mapSettings.showTectonicPlates" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">{{ mapSettings.showTectonicPlates ? 'Show' : 'Hide' }}</span>
                  </label>
                </div>
              </div>
              
              <!-- 3D Features Section -->
              <div class="setting-group">
                <label class="setting-label">3D Features</label>
                
                <!-- 3D Terrain Toggle -->
                <div class="toggle-group">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="mapSettings.show3DTerrain" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">3D Terrain {{ mapSettings.show3DTerrain ? 'On' : 'Off' }}</span>
                  </label>
                </div>
                
                <!-- 3D Buildings Toggle -->
                <div class="toggle-group" style="margin-top: 12px;">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="mapSettings.show3DBuildings" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">3D Buildings {{ mapSettings.show3DBuildings ? 'On' : 'Off' }}</span>
                  </label>
                </div>

                <!-- 3D Models Toggle -->
                <div class="toggle-group" style="margin-top: 12px;">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="mapSettings.show3DModels" />
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">3D Models {{ mapSettings.show3DModels ? 'On' : 'Off' }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <ModalWindow v-if="modalUrl" :url="modalUrl" :isOpen="isModalOpen && modalUrl" @close="isModalOpen = false" />
    <InfoModalWindow :content="infoData" :isOpen="isInfoModalOpen" @close="isInfoModalOpen = false" />
    <ImagesModal />
    <MoreInfoModal />
    
    <!-- Mobile Loading Modal -->
    <div v-if="loading && isMobile" class="mobile-loading-modal">
      <div class="loading-modal-content">
        <div class="loading-animation">
          <div class="loading-spinner"></div>
        </div>
        <h3 class="loading-title">Exploring the World</h3>
        <p class="loading-message">{{ currentLoadingMessage }}</p>
      </div>
    </div>
    
    <!-- Tablet & Mobile Settings Modal -->
    <div v-if="(isMobile || isTablet) && showMapSettings" class="settings-modal-overlay">
      <div class="settings-overlay-content">
        <div class="settings-overlay-header">
          <h3>Map Settings</h3>
          <button @click="toggleMapSettings" class="close-settings-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="settings-overlay-body">
          <!-- Map Style Selection -->
          <div class="setting-group">
            <label class="setting-label">Map Style</label>
            <div class="radio-group">
              <label class="radio-option">
                <input type="radio" name="mapStyle" value="light" v-model="mapSettings.style" />
                <span>Light</span>
              </label>
              <label class="radio-option">
                <input type="radio" name="mapStyle" value="satellite" v-model="mapSettings.style" />
                <span>Satellite</span>
              </label>
              <label class="radio-option">
                <input type="radio" name="mapStyle" value="terrain" v-model="mapSettings.style" />
                <span>Terrain</span>
              </label>
            </div>
          </div>
          
          <!-- Country Overlay Settings -->
          <div class="setting-group">
            <label class="setting-label">Country Overlay</label>
            <div class="radio-group">
              <label class="radio-option">
                <input type="radio" name="countryOverlay" value="none" v-model="mapSettings.countryOverlay" />
                <span>None</span>
              </label>
              <label class="radio-option">
                <input type="radio" name="countryOverlay" value="white" v-model="mapSettings.countryOverlay" />
                <span>White Borders</span>
              </label>
              <label class="radio-option">
                <input type="radio" name="countryOverlay" value="colored" v-model="mapSettings.countryOverlay" />
                <span>Colored</span>
              </label>
            </div>
          </div>
          
          <!-- Place Names Toggle -->
          <div class="setting-group">
            <label class="setting-label">Place Names</label>
            <div class="toggle-group">
              <label class="toggle-switch">
                <input type="checkbox" v-model="mapSettings.showPlaceNames" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">{{ mapSettings.showPlaceNames ? 'Show' : 'Hide' }}</span>
              </label>
            </div>
          </div>
          
          <!-- Tectonic Plates Toggle -->
          <div class="setting-group">
            <label class="setting-label">Tectonic Plates</label>
            <div class="toggle-group">
              <label class="toggle-switch">
                <input type="checkbox" v-model="mapSettings.showTectonicPlates" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">{{ mapSettings.showTectonicPlates ? 'Show' : 'Hide' }}</span>
              </label>
            </div>
          </div>
          
          <!-- 3D Features Section -->
          <div class="setting-group">
            <label class="setting-label">3D Features</label>
            
            <!-- 3D Terrain Toggle -->
            <div class="toggle-group">
              <label class="toggle-switch">
                <input type="checkbox" v-model="mapSettings.show3DTerrain" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">3D Terrain {{ mapSettings.show3DTerrain ? 'On' : 'Off' }}</span>
              </label>
            </div>
            
            <!-- 3D Buildings Toggle -->
            <div class="toggle-group" style="margin-top: 12px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="mapSettings.show3DBuildings" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">3D Buildings {{ mapSettings.show3DBuildings ? 'On' : 'Off' }}</span>
              </label>
            </div>

            <!-- 3D Models Toggle -->
            <div class="toggle-group" style="margin-top: 12px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="mapSettings.show3DModels" />
                <span class="toggle-slider"></span>
                <span class="toggle-text">3D Models {{ mapSettings.show3DModels ? 'On' : 'Off' }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Mobile Places Drawer -->
  <PlacesHistory 
    v-if="isMobile"
    :is-open="showPlacesDrawer"
    :show-global="!isAuthenticated" 
    @navigate-to-place="handleNavigateToPlace"
    @close-drawer="closePlacesDrawer"
  />
  
  <!-- Mobile Quiz Drawer -->
  <div v-if="isMobile && showQuizDrawer" class="quiz-drawer-overlay" :class="{ 'drawer-minimized': quizDrawerMinimized }">
    <!-- Minimized Header Bar (always visible when minimized) -->
    <div class="drawer-header-bar" @click="expandQuizDrawer" v-if="quizDrawerMinimized">
      <div class="drawer-header-content">
        <i class="fas fa-question-circle drawer-mini-icon"></i>
        <div class="drawer-mini-info">
          <h3 class="drawer-mini-title">Country Quiz</h3>
          <p class="drawer-mini-location">Test your geography knowledge</p>
        </div>
        <i class="fas fa-chevron-up drawer-expand-icon"></i>
      </div>
    </div>
    
    <!-- Full Drawer Content -->
    <div class="drawer-content" :class="{ 'drawer-hidden': quizDrawerMinimized }">
      <!-- Drawer Header with Controls -->
      <div class="drawer-header">
        <div class="drawer-controls">
          <button @click="minimizeQuizDrawer" class="drawer-minimize-btn" aria-label="Minimize">
            <i class="fas fa-chevron-down"></i>
          </button>
          <button @click="closeQuizDrawer" class="drawer-close-btn" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Drawer Header Title -->
        <div class="drawer-header-info">
          <div class="drawer-header-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <div class="drawer-title-section">
            <h2 class="drawer-main-title">Country Quiz</h2>
            <p class="drawer-subtitle">Test your geography knowledge</p>
          </div>
        </div>
      </div>
      
      <!-- Scrollable Quiz Content -->
      <div class="drawer-scrollable-content">
        <CountryQuiz
          @quiz-country-name="updateQuizCountryName"
          @quiz-type-changed="updateQuizType"
          @update-score="updateScore"
          ref="mobileQuiz"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ModalWindow from './ModalWindow.vue';
import CountryQuiz from './CountryQuiz.vue';
import InfoTab from './InfoTab.vue';
import InfoModalWindow from './InfoModalWindow.vue';
import ImagesModal from './ImagesModal.vue';
import MoreInfoModal from './MoreInfoModal.vue';
import PlacesHistory from './PlacesHistory.vue';

export default {
  name: 'SidebarMenu',
  emits: ['toggle', 'quiz-country-name', 'quiz-type-changed', 'update-active-tab', 'quiz-complete', 'next-clue', 'map-settings-changed', 'next-amazing-place', 'navigate-to-place'],
  components: {
    ModalWindow,
    CountryQuiz,
    InfoTab,
    InfoModalWindow,
    ImagesModal,
    MoreInfoModal,
    PlacesHistory,
  },
  data() {
    return {
      isOpen: true,
      activeTab: 'info',
      infoData: {},
      countryCode: '',
      isModalOpen: false,
      isInfoModalOpen: false,
      modalUrl: '',
      loading: false,
      totalScore: 0,
      showPlacesDrawer: false,
      showQuizDrawer: false,
      quizDrawerMinimized: false,
      isMobile: window.innerWidth <= 768, // Initial check for mobile
      isSmallMobile: window.innerWidth <= 480, // Check for small mobile screens
      isTablet: window.innerWidth > 768 && window.innerWidth <= 1024, // Check for tablet screens
      loadingMessages: [
        "🔍 Researching about place information...",
        "📜 Studying its rich history...",
        "🏛️ Finding out famous landmarks...",
        "🌿 Investigating endemic species...",
        "🌟 Figuring out who the famous people are...",
      ],
      currentLoadingMessage: "",
      loadingInterval: null,
      isInitialLoad: true, // Track if this is the first load
      showMapSettings: false,
      mapSettings: {
        style: 'satellite',
        countryOverlay: 'white',
        showPlaceNames: true,
        showTectonicPlates: false,
        show3DTerrain: true,
        show3DBuildings: false,
        show3DModels: false
      }
    };
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
    clearInterval(this.loadingInterval);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
      this.isSmallMobile = window.innerWidth <= 480;
      this.isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
    },
    toggleSidebar() {
      this.isOpen = !this.isOpen;
      this.$emit('toggle', this.isOpen);
    },
    updateInfo(data) {
      this.infoData = data;
      this.countryCode = data.country_code.toLowerCase();
      this.activeTab = 'info';
      this.loading = false;
      this.isInitialLoad = false; // No longer initial load once we have data
      
      // On small mobile screens, show the info modal directly
      if (this.isSmallMobile && data && Object.keys(data).length > 0) {
        this.isInfoModalOpen = true;
      }
    },
    startLoadingMessages(context = null) {
      // Create place-specific loading messages if we have place context
      const messages = this.createLoadingMessages(context);
      
      let index = 0;
      this.currentLoadingMessage = messages[index];
      console.log(this.currentLoadingMessage);
      this.loadingInterval = setInterval(() => {
        index = (index + 1) % messages.length;
        this.currentLoadingMessage = messages[index];
        console.log(this.currentLoadingMessage);
      }, 3000); // Change message every 3 seconds
    },
    
    createLoadingMessages(context) {
      if (!context || !context.placeName) {
        // Return default messages if no context
        return this.loadingMessages;
      }
      
      const placeName = context.placeName;
      return [
        `🔍 Researching about ${placeName}...`,
        `📜 Studying ${placeName}'s rich history...`,
        `🏛️ Finding famous landmarks in ${placeName}...`,
        `🌿 Investigating ${placeName}'s endemic species...`,
        `🌟 Discovering who the famous people from ${placeName} are...`,
        `🗺️ Exploring ${placeName}'s geography...`,
        `🍽️ Learning about ${placeName}'s traditional foods...`
      ];
    },
    openModal(url) {
      if (url && url !== '#') {
        this.modalUrl = url;
        this.isModalOpen = true;
      } else {
        // If no valid URL, open the info modal instead
        this.isInfoModalOpen = true;
      }
    },
    setLoading(isLoading, context = null) {
      this.loading = isLoading;
      if (isLoading) {
        this.infoData = {};
        this.startLoadingMessages(context);
      } else {
        clearInterval(this.loadingInterval);
      }
    },
    updateQuizCountryName(name) {
      this.$emit('quiz-country-name', name);
    },
    updateQuizType(type) {
      console.log('SidebarMenu: updateQuizType called with:', type);
      this.$emit('quiz-type-changed', type);
    },
    handleWrongGuess() {
      // Forward wrong guess to the quiz component
      if (this.isMobile && this.$refs.mobileQuiz) {
        this.$refs.mobileQuiz.handleWrongGuess();
      } else if (this.$refs.quiz) {
        this.$refs.quiz.handleWrongGuess();
      }
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      this.$emit('update-active-tab', tab);
      
      // Show quiz drawer on mobile when quiz tab is selected
      if (this.isMobile && tab === 'quiz') {
        this.showQuizDrawer = true;
        this.quizDrawerMinimized = false;
      } else {
        this.showQuizDrawer = false;
      }
    },
    completeQuiz() {
      // Auto-expand quiz drawer on mobile when quiz is completed
      if (this.isMobile && this.showQuizDrawer) {
        this.quizDrawerMinimized = false;
      }
      
      if (this.isMobile && this.$refs.mobileQuiz) {
        this.$refs.mobileQuiz.completeQuiz();
      } else if (this.$refs.quiz) {
        this.$refs.quiz.completeQuiz();
      }
    },
    updateScore(score) {
      this.totalScore += score;
    },
    nextClue() {
      // Auto-expand quiz drawer on mobile when next clue is shown
      if (this.isMobile && this.showQuizDrawer) {
        this.quizDrawerMinimized = false;
      }
      
      if (this.isMobile && this.$refs.mobileQuiz) {
        this.$refs.mobileQuiz.nextClue();
      } else if (this.$refs.quiz) {
        this.$refs.quiz.nextClue();
      }
    },
    openInfoModal() {
      this.isInfoModalOpen = true;
    },
    toggleMapSettings() {
      this.showMapSettings = !this.showMapSettings;
    },
    goToNextAmazingPlace() {
      this.$emit('next-amazing-place');
    },
    closeInfo() {
      this.isInfoModalOpen = false;
    },
    openPlacesDrawer() {
      if (this.isMobile) {
        this.showPlacesDrawer = true;
      } else {
        this.setActiveTab('history');
      }
    },
    closePlacesDrawer() {
      this.showPlacesDrawer = false;
    },
    openQuizDrawer() {
      if (this.isMobile) {
        this.showQuizDrawer = true;
        this.quizDrawerMinimized = false;
      } else {
        this.setActiveTab('quiz');
      }
    },
    closeQuizDrawer() {
      this.showQuizDrawer = false;
    },
    minimizeQuizDrawer() {
      this.quizDrawerMinimized = true;
    },
    expandQuizDrawer() {
      this.quizDrawerMinimized = false;
    },
    handleNavigateToPlace(place) {
      // Emit event to parent component (App.vue) to navigate to the place
      this.$emit('navigate-to-place', place);
    },
  },
  watch: {
    mapSettings: {
      handler(newSettings) {
        this.$emit('map-settings-changed', newSettings);
      },
      deep: true
    }
  },
  computed: {
    flagUrl() {
      return `https://flagpedia.net/data/flags/w580/${this.countryCode}.webp?v=un`;
    },
    hasInfoData() {
      return Object.keys(this.infoData).length > 0;
    },
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
  },
};
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

/* Sidebar Background */
.sidebar-wrapper {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-right: 1px solid #e2e8f0;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
}

/* Header */
.sidebar-header {
  background: white;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.toggle-btn {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Sidebar Content */
.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  overflow: hidden; /* Prevent overflow on parent */
}

/* Modern Tabs */
.tabs-container {
  padding: 20px 20px 0;
  position: relative;
}

.tab-selector {
  display: flex;
  background: #f1f5f9;
  border-radius: 16px;
  padding: 4px;
  position: relative;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 500;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.tab-button.active {
  color: #1e293b;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tab-button i {
  font-size: 16px;
}

.tab-button span {
  font-size: 14px;
  font-weight: 600;
}

/* Tab Indicator */
.tab-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px 2px 0 0;
  transition: all 0.3s ease;
  width: 50%; /* Default for 2 tabs (desktop) */
  transform: translateX(0);
}

/* Tablet layout with 3 tabs */
@media (min-width: 769px) and (max-width: 1366px) {
  .tab-indicator {
    width: 33.33%; /* 3 tabs on tablets */
  }
  
  .tab-indicator.quiz-active {
    transform: translateX(100%);
  }
  
  .tab-indicator.history-active {
    transform: translateX(200%);
  }
  
  .tab-indicator.settings-active {
    transform: translateX(300%);
  }
}

/* Desktop layout with 2 or more tabs */
@media (min-width: 1367px) {
  .tab-indicator.quiz-active {
    transform: translateX(100%);
  }
  
  .tab-indicator.history-active {
    transform: translateX(200%);
  }
}

/* Scrollable Content Wrapper */
.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Content Area */
.content-area {
  padding: 20px;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 20px;
}

.modern-loader {
  width: 48px;
  height: 48px;
  border: 4px solid #f1f5f9;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 14px;
  text-align: center;
  max-width: 250px;
  line-height: 1.5;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  height: 300px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-icon i {
  font-size: 36px;
  color: white;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  max-width: 280px;
}

/* Auth Required State */
.auth-required-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  height: 300px;
}

.auth-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.auth-icon i {
  font-size: 36px;
  color: white;
}

.auth-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.auth-text {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  max-width: 280px;
}

/* Content Containers */
.info-content,
.quiz-content,
.history-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Legacy loader for compatibility */
.loader {
  border: 16px solid #f3f3f3;
  border-top: 16px solid #3498db;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  animation: spin 1.5s linear infinite;
}

/* Tablet responsive adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
  .settings-accordion-content {
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 16px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  
  .setting-group {
    margin-bottom: 20px;
  }
  
  .radio-group {
    gap: 6px;
  }
  
  .radio-option {
    padding: 10px;
    font-size: 13px;
  }
  
  .setting-label {
    font-size: 13px;
    margin-bottom: 8px;
  }
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .tabs-container {
    padding: 15px;
  }
  
  .tab-button {
    padding: 10px 12px;
    gap: 6px;
  }
  
  .tab-button span {
    font-size: 13px;
  }
  
  .content-area {
    padding: 15px;
  }
  
  .empty-state {
    padding: 30px 15px;
    height: 250px;
  }
  
  .empty-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
  }
  
  .empty-icon i {
    font-size: 28px;
  }
  
  .empty-title {
    font-size: 18px;
  }
  
  .settings-accordion-content {
    max-height: 50vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px 15px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }
  
  .setting-group {
    margin-bottom: 16px;
  }
  
  .radio-option {
    padding: 8px 10px;
  }
  
  .setting-label {
    font-size: 12px;
  }
}

/* Sidebar Footer - Fixed to bottom */
.sidebar-footer {
  position: sticky;
  bottom: 0;
  border-top: 1px solid #e2e8f0;
  background: white;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

/* Settings Accordion */
.settings-accordion {
  width: 100%;
}

.settings-accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border: none;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.settings-accordion-header:hover {
  background: #f1f5f9;
  color: #475569;
}

.settings-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.settings-header-content i {
  font-size: 16px;
}

.settings-chevron {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.settings-accordion-content {
  padding: 16px 20px;
  background: #fafbfc;
  border-top: 1px solid #e2e8f0;
  animation: slideDown 0.3s ease-out;
  /* Better scrolling support for all devices */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  /* Allow scrolling when content overflows */
  max-height: 50vh;
  position: relative;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

/* Setting Groups */
.setting-group {
  margin-bottom: 24px;
}

.setting-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

/* Radio Groups */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.radio-option:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.radio-option input[type="radio"] {
  margin: 0;
  accent-color: #667eea;
}

.radio-option span {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

/* Toggle Switch */
.toggle-group {
  display: flex;
  align-items: center;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.toggle-switch input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  width: 48px;
  height: 24px;
  background: #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.toggle-slider:before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-switch input:checked + .toggle-slider {
  background: #667eea;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(24px);
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

/* Mobile Tab Buttons for Small Screens */
.mobile-tab-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  min-width: 60px;
}

.mobile-tab-btn.active {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.mobile-tab-btn:hover {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.1);
}

.mobile-tab-btn i {
  margin-bottom: 2px;
}

.mobile-tab-btn span {
  font-weight: 500;
}

/* Mobile Loading Modal */
.mobile-loading-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
}

.loading-modal-content {
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  max-width: 300px;
  margin: 80px 20px 20px 20px; /* Add top margin to avoid search bar overlap on mobile */
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.loading-animation {
  margin-bottom: 20px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f1f5f9;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

.loading-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.loading-message {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Tablet & Mobile Settings Modal */
.settings-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  padding: 20px;
}

.settings-overlay-content {
  background: white;
  border-radius: 12px;
  max-height: 85vh;
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.settings-overlay-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.settings-overlay-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.close-settings-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
}

.close-settings-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.settings-overlay-body {
  padding: 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
}

/* Tablet specific styles */
@media (min-width: 481px) and (max-width: 1024px) {
  .settings-modal-overlay {
    padding: 80px 20px 20px 20px; /* Add top padding to avoid search bar overlap */
  }
  
  .settings-overlay-content {
    max-width: 600px;
    max-height: calc(90vh - 64px); /* Reduce height to account for top margin */
  }
  
  .settings-overlay-body {
    padding: 28px;
  }
}

/* Small mobile specific styles */
@media (max-width: 480px) {
  .settings-modal-overlay {
    padding: 80px 16px 16px 16px; /* Add top padding to avoid search bar overlap */
  }
  
  .settings-overlay-content {
    max-width: none;
    max-height: calc(90vh - 64px); /* Reduce height to account for top margin */
  }
  
  .settings-overlay-header {
    padding: 16px 20px;
  }
  
  .settings-overlay-header h3 {
    font-size: 18px;
  }
  
  .settings-overlay-body {
    padding: 20px;
  }
}

/* Next Amazing Place Button Styles */
.next-place-container {
  padding: 12px 20px;
}

.next-place-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.next-place-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.next-place-btn:active {
  transform: translateY(0);
}

.next-place-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.next-place-btn:hover::before {
  left: 100%;
}

.next-place-btn i:first-child {
  font-size: 18px;
}

.next-place-btn i:last-child {
  font-size: 14px;
  opacity: 0.8;
}

.next-place-btn span {
  flex: 1;
  text-align: center;
}

/* Secondary Next Place Button (Green) */
.next-place-btn-secondary {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.next-place-btn-secondary:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.next-place-btn-secondary:active {
  transform: translateY(0);
}

.next-place-btn-secondary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s;
}

.next-place-btn-secondary:hover::before {
  left: 100%;
}

.next-place-btn-secondary i:first-child {
  font-size: 18px;
}

.next-place-btn-secondary i:last-child {
  font-size: 14px;
  opacity: 0.8;
}

.next-place-btn-secondary span {
  flex: 1;
  text-align: center;
}

/* Mobile Next Place Button */
.mobile-next-place-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  min-width: 60px;
}

.mobile-next-place-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.mobile-next-place-btn i {
  margin-bottom: 2px;
}

.mobile-next-place-btn span {
  font-weight: 500;
}

/* Mobile Quiz Drawer Styles */
.quiz-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.quiz-drawer-overlay.drawer-minimized {
  background: transparent;
  backdrop-filter: none;
  pointer-events: none;
}

.quiz-drawer-overlay.drawer-minimized .drawer-header-bar {
  pointer-events: auto;
}

.drawer-header-bar {
  position: fixed;
  bottom: 60px;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 12px 20px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 999999;
}

.drawer-header-bar:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
}

.drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.drawer-mini-icon {
  font-size: 20px;
  color: white;
}

.drawer-mini-info {
  flex: 1;
  text-align: center;
  margin: 0 16px;
}

.drawer-mini-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: white;
}

.drawer-mini-location {
  font-size: 12px;
  margin: 2px 0 0 0;
  color: rgba(255, 255, 255, 0.8);
}

.drawer-expand-icon {
  font-size: 16px;
  color: white;
  transition: transform 0.3s ease;
}

.drawer-content {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 20%;
  background: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 999999;
  animation: slideUpFromBottom 0.3s ease-out;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.drawer-content.drawer-hidden {
  transform: translateY(100%);
  opacity: 0;
  pointer-events: none;
}

.drawer-header {
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.drawer-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.drawer-minimize-btn,
.drawer-close-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.drawer-minimize-btn:hover,
.drawer-close-btn:hover {
  background: #e5e7eb;
  color: #374151;
  transform: scale(1.05);
}

.drawer-header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.drawer-header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.drawer-title-section {
  flex: 1;
}

.drawer-main-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.drawer-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0 0;
  line-height: 1.3;
}

.drawer-scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

@keyframes slideUpFromBottom {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* Custom scrollbar for quiz drawer */
.drawer-scrollable-content::-webkit-scrollbar {
  width: 4px;
}

.drawer-scrollable-content::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.drawer-scrollable-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.drawer-scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
